import { useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { type ActionFunctionArgs, type LoaderFunctionArgs, redirect } from '@remix-run/node';
import { useActionData, useLoaderData, useSearchParams } from '@remix-run/react';
import { useEffect } from 'react';
import { typedjson } from 'remix-typedjson';
import { toast } from 'sonner';
import { getMetaTitle } from '#buyer-portal/i18n/i18n.server';
import { verifyLogin } from '#buyer-portal/models/shop-user.server';
import { getUserId, handleNewSession } from '#buyer-portal/session.server';
import { Login, loginSchema } from '#common/features/auth/Login/index.js';
import { type LayoutHandle } from '#common/hooks/useLayoutTitleKey.ts';
import { commitFlashSession, getFlashSession } from '#common/utils/flashSession.server.js';
import { checkHoneypot } from '#common/utils/honeypot.server';
import { safeRedirect } from '#common/utils/misc';

export const loader = async ({ request }: LoaderFunctionArgs) => {
    const session = await getFlashSession(request.headers.get('cookie'));
    const successMessage = session.get('success');

    const userId = await getUserId(request);
    if (userId) {
        return redirect('/account');
    }
    return typedjson({ title: await getMetaTitle(request, 'login'), successMessage }, {
        headers: { 'Set-Cookie': await commitFlashSession(session) }
    });
};

export const action = async ({ request }: ActionFunctionArgs) => {
    const formData = await request.formData();
    await checkHoneypot(formData);

    const submission = parseWithZod(formData, { schema: loginSchema });
    if (submission.status !== 'success') {
        return typedjson(
            { result: submission.reply() },
            { status: submission.status === 'error' ? 400 : 200 }
        );
    }

    const redirectTo = safeRedirect(submission.value.redirectTo, '/');

    const user = await verifyLogin(submission.value.email, submission.value.password);

    if (!user) {
        return typedjson(
            submission.reply({
                fieldErrors: {
                    email: [ 'Invalid email or password' ],
                    password: [ 'Invalid email or password' ]
                }
            }),
            { status: 400 }
        );
    }

    return handleNewSession({
        redirectTo,
        remember: submission.value.remember ?? false,
        request,
        userId: user.id,
        userEmail: user.email
    });
};

export default function LoginPage() {
    const [ searchParams ] = useSearchParams();
    const redirectTo = searchParams.get('redirectTo') || '/account';
    const { successMessage } = useLoaderData<typeof loader>();
    const actionData = useActionData<typeof action>();
    const [ form, fields ] = useForm({
        lastResult: actionData,
        constraint: getZodConstraint(loginSchema),
        shouldValidate: 'onBlur',
        shouldRevalidate: 'onInput',
        onValidate({ formData }) {
            return parseWithZod(formData, { schema: loginSchema });
        }
    });

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
        }
    }, [ successMessage ]);

    return <Login fields={fields} form={form} redirectTo={redirectTo} createAccountUrl='/register' />;
}

export const handle: LayoutHandle & { i18n: string[] } = { layoutTitleKey: 'login', i18n: [ 'login' ] };
